import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import CustomCheckbox from "../../../../Overview/components/Checkbox";
import PrimaryChip from "../../../../../Components/PrimaryChip";
import { PrimaryFormActionButton } from "../../../../../Components/StyledComponent";
import { useRULBuckets } from "../../utils/rulBuckets";

// Options come from org meta (useRULBuckets().months) and end with "RUL Unavailable".
// Option values are bucket names (the API wire labels); the UI shows displayLabel.

// ─── Component ────────────────────────────────────────────────────────────────
const RULCategoryFilter = ({ onChange }) => {
  const rulCategories = useRULBuckets().months;
  const [tempSelected, setTempSelected] = useState([]);
  const [appliedSelected, setAppliedSelected] = useState([]);

  const handleSelection = (name) => {
    setTempSelected((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const handleClearSelection = () => setTempSelected([]);

  const handleApply = () => {
    setAppliedSelected(tempSelected);
    if (onChange) onChange(tempSelected);
  };

  const handleChipDelete = (item) => {
    const next = appliedSelected.filter((x) => x !== item);
    setAppliedSelected(next);
    setTempSelected(next);
    if (onChange) onChange(next);
  };

  return (
    <Accordion
      sx={{
        boxShadow: "none",
        borderBottom: "1px solid #E6E6E6",
        my: 0,
        "&:before": { display: "none" },
        "&.Mui-expanded": { my: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          paddingTop: "18px",
          paddingBottom: "18px",
          alignItems: "flex-start",
          "&.Mui-expanded": { minHeight: "auto" },
          "& .MuiAccordionSummary-content": {
            margin: 0,
            alignItems: "flex-start",
            gap: 1,
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            variant="fw400s14c000000"
            sx={{ lineHeight: "16px", color: "#1C1C1C", mr: 1 }}
          >
            RUL Range
          </Typography>
          {appliedSelected.length > 0 && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
              onClick={(e) => e.stopPropagation()}
            >
              {appliedSelected.map((name) => {
                const cat = rulCategories.find((c) => c.name === name);
                return (
                  <PrimaryChip
                    key={name}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {cat && (
                          <Box
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              bgcolor: cat.color,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        {cat?.displayLabel ?? name}
                      </Box>
                    }
                    tooltipTitle={cat?.displayLabel ?? name}
                    tooltipPlacement="bottom"
                    onDelete={() => handleChipDelete(name)}
                    deleteIcon={<CloseIcon />}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ maxHeight: "200px", overflowY: "auto" }}>
            {rulCategories.map((cat) => (
              <Box
                key={cat.name}
                data-testid={`rul-category-filter-option-${cat.name}`}
                onClick={() => handleSelection(cat.name)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  py: 0.5,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#FAFAFA" },
                  borderRadius: "4px",
                  px: 0.5,
                }}
              >
                <CustomCheckbox
                  checked={tempSelected.includes(cat.name)}
                  onChange={() => handleSelection(cat.name)}
                  onClick={(e) => e.stopPropagation()}
                />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: cat.color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="fw400s12lh24c3D4B5A">
                  {cat.displayLabel}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.5,
              mt: 1,
            }}
          >
            <Typography
              variant="fw500s12lh24c6D72F6"
              sx={{
                cursor: tempSelected.length > 0 ? "pointer" : "not-allowed",
                color: tempSelected.length > 0 ? "#6D72F6" : "#8F8F8F",
              }}
              onClick={() => tempSelected.length > 0 && handleClearSelection()}
            >
              Clear Selection
            </Typography>
            <PrimaryFormActionButton
              onClick={handleApply}
              sx={{ py: 0.75, px: 2, fontSize: "12px" }}
            >
              Apply
            </PrimaryFormActionButton>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default RULCategoryFilter;
